import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../App';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const PLANS = [
  {
    key: 'free',
    name: 'Free',
    eyebrow: 'Bireysel keşif',
    price: { monthly: '₺0', annual: '₺0' },
    priceNote: 'Sonsuza kadar ücretsiz',
    desc: 'Tek kullanıcılı temel kullanım için.',
    cta: 'Ücretsiz başla',
    features: ['1 kullanıcı', '1 proje', 'Görev panosu (Pano · Liste)', 'Temel raporlar'],
  },
  {
    key: 'starter',
    name: 'Starter',
    eyebrow: 'Küçük ekipler',
    price: { monthly: '₺299', annual: '₺249' },
    priceNote: 'kullanıcı / ay',
    desc: 'Küçük ekipler için temel proje yönetimi.',
    cta: "Starter'ı seç",
    features: ['5 kullanıcıya kadar', '1 proje', 'Tüm pano görünümleri', 'E-posta desteği', 'Temel raporlar', 'CSV dışa aktarma'],
  },
  {
    key: 'growth',
    name: 'Growth',
    eyebrow: 'En çok tercih edilen',
    price: { monthly: '₺799', annual: '₺665' },
    priceNote: 'kullanıcı / ay',
    desc: 'Büyüyen ekipler için onay süreçleri ve gelişmiş raporlama.',
    cta: "Growth'u seç",
    popular: true,
    features: ['15 kullanıcıya kadar', '3 proje', 'Onay süreçleri', 'İş akışı tasarımı', 'Gelişmiş raporlar', 'Aktivite günlüğü', 'Bildirim yönetimi', 'API erişimi'],
  },
  {
    key: 'scale',
    name: 'Scale',
    eyebrow: 'Büyük ekipler',
    price: { monthly: '₺1.999', annual: '₺1.665' },
    priceNote: 'kullanıcı / ay',
    desc: 'Çoklu proje yöneten organizasyonlar için.',
    cta: "Scale'i seç",
    features: ['50 kullanıcıya kadar', '10 proje', 'Öncelikli destek', 'Rol bazlı erişim (RBAC)', 'Özel iş akışı şablonları', 'Tüm raporlar', 'Webhook entegrasyonu', '99.9% SLA'],
  },
  {
    key: 'unlimited',
    name: 'Unlimited',
    eyebrow: 'Kurumsal',
    price: { monthly: 'Teklif', annual: 'Teklif' },
    priceNote: 'özel anlaşma',
    desc: 'Sınırsız ölçek, özel altyapı ve birebir destek.',
    cta: 'İletişime geç',
    features: ['Sınırsız kullanıcı', 'Sınırsız proje', 'SSO (SAML / OIDC)', 'Birebir hesap yöneticisi', 'Özel SLA', 'Yerinde kurulum seçeneği', 'KVKK danışmanlığı', 'Özel entegrasyonlar'],
  },
];

const COMPARISON = [
  {
    group: 'Kapasite',
    rows: [
      { label: 'Kullanıcı sayısı', values: ['1', '5', '15', '50', 'Sınırsız'] },
      { label: 'Proje sayısı', values: ['1', '1', '3', '10', 'Sınırsız'] },
      { label: 'Dosya depolama', values: ['1 GB', '10 GB', '50 GB', '250 GB', 'Sınırsız'] },
    ],
  },
  {
    group: 'Görev yönetimi',
    rows: [
      { label: 'Kanban panosu', values: [true, true, true, true, true] },
      { label: 'Liste görünümü', values: [true, true, true, true, true] },
      { label: 'Zaman çizelgesi', values: [false, true, true, true, true] },
      { label: 'Etiket & öncelik', values: [true, true, true, true, true] },
      { label: 'Toplu işlemler', values: [false, false, true, true, true] },
    ],
  },
  {
    group: 'İş akışları & onaylar',
    rows: [
      { label: 'Standart iş akışları', values: [true, true, true, true, true] },
      { label: 'Özel iş akışı tasarımı', values: [false, false, true, true, true] },
      { label: 'Çok adımlı onay zincirleri', values: [false, false, true, true, true] },
      { label: 'Onay şablonları', values: [false, false, false, true, true] },
    ],
  },
  {
    group: 'Raporlama',
    rows: [
      { label: 'Temel KPI raporları', values: [true, true, true, true, true] },
      { label: 'Gelişmiş analitik', values: [false, false, true, true, true] },
      { label: 'CSV dışa aktarma', values: [true, true, true, true, true] },
      { label: 'Özel rapor şablonları', values: [false, false, false, true, true] },
    ],
  },
  {
    group: 'Güvenlik & yönetim',
    rows: [
      { label: 'Aktivite günlüğü', values: [false, true, true, true, true] },
      { label: 'Rol bazlı erişim (RBAC)', values: [false, false, false, true, true] },
      { label: 'SSO (SAML / OIDC)', values: [false, false, false, false, true] },
      { label: 'KVKK uyum desteği', values: [false, false, true, true, true] },
      { label: 'SLA garantisi', values: ['—', '—', '—', '99.9%', 'Özel'] },
    ],
  },
  {
    group: 'Destek',
    rows: [
      { label: 'Yardım merkezi', values: [true, true, true, true, true] },
      { label: 'E-posta desteği', values: [false, true, true, true, true] },
      { label: 'Öncelikli destek', values: [false, false, false, true, true] },
      { label: 'Birebir hesap yöneticisi', values: [false, false, false, false, true] },
    ],
  },
];

const FAQ_ITEMS = [
  { q: 'Plan değiştirebilir miyim?', a: 'Evet. İstediğiniz zaman planınızı yükseltebilir veya düşürebilirsiniz. Yıllık planda kalan kullanım gün bazlı orantılanır.' },
  { q: 'Ödeme yöntemleri neler?', a: 'Kredi kartı, banka transferi (kurumsal planlarda) ve e-fatura ile ödeme kabul ediyoruz. Tüm ödemeler TRY cinsinden, KDV dahildir.' },
  { q: 'Ücretsiz deneme süresi var mı?', a: '14 gün boyunca tüm Growth plan özelliklerini kredi kartı gerekmeden deneyebilirsiniz.' },
  { q: 'Verilerim nerede saklanıyor?', a: 'Tüm veriler Türkiye lokasyonlu sunucularda, KVKK uyumlu altyapıda şifrelenerek depolanır. Kurumsal planlarda yerinde kurulum seçeneği mevcuttur.' },
  { q: 'İptal politikası nedir?', a: 'Aylık planlar dilediğiniz zaman iptal edilebilir; iptal sonrası dönem sonuna kadar erişiminiz devam eder. Yıllık planlarda 30 gün koşulsuz iade garantisi sunuyoruz.' },
  { q: 'Mevcut araçlardan veri içe aktarabilir miyim?', a: 'Evet. CSV içe aktarma standart olarak gelir. Jira, Trello ve Asana için özel migrasyon araçları kurumsal planlarda destek ekibimiz tarafından sağlanır.' },
];

const ENTERPRISE_TAGS = ['SSO (SAML / OIDC)', 'Yerinde kurulum', 'Özel SLA', '7/24 destek', 'KVKK danışmanlığı', 'Özel entegrasyonlar'];

const GUARANTEES = [
  { t: '14 gün ücretsiz deneme', d: 'Kredi kartı gerekmez' },
  { t: '30 gün iade garantisi', d: 'Yıllık planlarda koşulsuz' },
  { t: 'Anında iptal', d: 'Aylık planlar her an iptal edilebilir' },
  { t: 'KVKK uyumlu', d: 'TR lokasyonlu altyapı' },
];

const ACCENT = '#1E5FA8';
const ACCENT_WEAK = '#e6eefa';

const fontStack = { fontFamily: "'Inter Tight', ui-sans-serif, system-ui, -apple-system, sans-serif" };
const monoStack = { fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace", fontFeatureSettings: '"tnum" 1, "zero" 1' };

export default function Pricing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeWorkspace, refresh } = useWorkspace();
  const { user } = useAuth();
  const [cycle, setCycle] = useState('annual');
  const [submitting, setSubmitting] = useState(null);
  const [openFAQIdx, setOpenFAQIdx] = useState(0);

  const currentPlanKey = activeWorkspace?.plan || 'free';

  const reasonLabel = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('reason') === 'quota' ? 'Plan limitiniz doldu — yükseltmek için bir plan seçin' : null;
  }, [location.search]);

  const handlePlanClick = async (plan) => {
    if (plan.key === 'unlimited') {
      window.location.href = 'mailto:satis@onxflow.com?subject=Kurumsal%20Plan%20Talebi';
      return;
    }
    if (plan.key === currentPlanKey) {
      toast.info('Bu plan zaten aktif');
      return;
    }
    if (plan.key === 'free') {
      toast.info('Free plan için ödeme gerekmez');
      return;
    }
    if (!activeWorkspace) {
      toast.error('Önce bir workspace seçmelisiniz');
      return;
    }
    setSubmitting(plan.key);
    try {
      const response = await axios.post(`${API}/billing/checkout`, {
        workspaceId: activeWorkspace.id,
        plan: plan.key,
        billingCycle: cycle,
      });
      const url = response.data?.checkoutUrl || response.data?.paymentPageUrl;
      if (url) {
        window.location.href = url;
        return;
      }
      toast.success('Plan başarıyla aktifleştirildi (sandbox)');
      await refresh();
      navigate('/settings/billing');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (detail?.message || 'Ödeme başlatılamadı');
      toast.error(message);
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div
      className="bg-white"
      style={{
        ...fontStack,
        '--accent': ACCENT,
        '--accent-weak': ACCENT_WEAK,
        color: '#0f172a',
        minHeight: '100vh',
      }}
    >
      <PricingNav user={user} />

      {reasonLabel && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-[13px] py-2.5 px-6 text-center">
          {reasonLabel}
        </div>
      )}

      <PricingHero cycle={cycle} setCycle={setCycle} />

      <section className="bg-white pb-24">
        <div className="max-w-[1280px] mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {PLANS.map((p) => (
              <PlanCard
                key={p.key}
                plan={p}
                cycle={cycle}
                isCurrent={p.key === currentPlanKey}
                isSubmitting={submitting === p.key}
                onClick={() => handlePlanClick(p)}
              />
            ))}
          </div>
          <div className="mt-8 text-center uppercase text-[10.5px] tracking-[0.14em] text-slate-500" style={monoStack}>
            Tüm fiyatlar TRY · KDV dahil · İstediğiniz zaman iptal edebilirsiniz
          </div>
        </div>
      </section>

      <ComparisonTable />
      <EnterpriseBand />
      <FAQSection openIdx={openFAQIdx} setOpenIdx={setOpenFAQIdx} />
      <GuaranteeStrip />
      <ClosingCTA user={user} />
      <PricingFooter />
    </div>
  );
}

function PricingNav({ user }) {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/75 border-b border-black/5">
      <div className="max-w-[1280px] mx-auto px-8 h-16 flex items-center justify-between">
        <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2.5 group">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-[15px] tracking-tight"
            style={{ background: ACCENT }}
          >
            X
          </div>
          <div className="leading-none">
            <div className="font-bold text-[16px] tracking-tight text-slate-900">OnxFlow</div>
            <div className="text-[9px] uppercase tracking-[0.14em] text-slate-500 mt-0.5" style={monoStack}>
              Proje Yönetimi
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-[14px] text-slate-700">
          <Link to={user ? '/dashboard' : '/'} className="hover:text-slate-950 transition">
            Ürün
          </Link>
          <Link to="/pricing" className="hover:text-slate-950 transition" style={{ color: '#0f172a', fontWeight: 600 }}>
            Fiyatlandırma
          </Link>
          <a href="mailto:destek@onxflow.com" className="hover:text-slate-950 transition">
            İletişim
          </a>
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <Link
              to="/dashboard"
              className="hidden sm:inline-flex items-center h-9 px-4 text-[14px] font-medium text-slate-700 hover:text-slate-950 transition"
            >
              Dashboard
            </Link>
          ) : (
            <Link
              to="/"
              className="hidden sm:inline-flex items-center h-9 px-4 text-[14px] font-medium text-slate-700 hover:text-slate-950 transition"
            >
              Giriş yap
            </Link>
          )}
          <Link
            to={user ? '/dashboard' : '/'}
            className="inline-flex items-center h-9 px-4 rounded-lg text-[13.5px] font-semibold text-white transition hover:brightness-110 shadow-sm"
            style={{ background: ACCENT }}
          >
            {user ? 'Uygulamaya dön' : 'Ücretsiz başla'}
          </Link>
        </div>
      </div>
    </header>
  );
}

function PricingHero({ cycle, setCycle }) {
  return (
    <section className="relative overflow-hidden bg-white">
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          backgroundImage:
            'linear-gradient(to right, #0a0e1408 1px, transparent 1px), linear-gradient(to bottom, #0a0e1408 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, #000 40%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, #000 40%, transparent 80%)',
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          top: '-180px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '900px',
          height: '500px',
          background: `radial-gradient(ellipse, color-mix(in oklch, ${ACCENT} 18%, transparent), transparent 60%)`,
          filter: 'blur(60px)',
        }}
      />
      <div className="relative max-w-[1280px] mx-auto px-8 pt-20 pb-12 text-center">
        <div className="inline-flex items-center gap-2 h-7 px-3 rounded-full border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: ACCENT, boxShadow: `0 0 6px ${ACCENT}` }}></span>
          <span className="uppercase text-[10.5px] tracking-[0.16em] text-slate-700" style={monoStack}>
            Şeffaf fiyatlandırma
          </span>
        </div>
        <h1 className="mt-7 text-[60px] md:text-[80px] leading-[0.96] tracking-[-0.035em] font-semibold text-slate-950 mx-auto max-w-[1000px]">
          Ekibinize{' '}
          <span
            className="italic font-light"
            style={{
              background: `linear-gradient(90deg, ${ACCENT}, color-mix(in oklch, ${ACCENT} 55%, #6c4ad9))`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontFamily: 'Arial',
              fontSize: '80px',
              letterSpacing: '0px',
            }}
          >
            uygun
          </span>{' '}
          ölçek.
        </h1>
        <p className="mt-7 text-[18px] text-slate-600 max-w-[620px] mx-auto leading-[1.55]">
          Bireysel kullanımdan kurumsal ölçeğe kadar şeffaf fiyatlandırma. Tüm planlar KDV dahil, Türkiye lokasyonlu altyapıda.
        </p>

        <div className="mt-9 inline-flex p-1 rounded-xl bg-slate-100 border border-slate-200">
          <button
            type="button"
            onClick={() => setCycle('monthly')}
            className={`h-9 px-5 rounded-lg text-[13.5px] font-semibold transition ${
              cycle === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
            }`}
          >
            Aylık
          </button>
          <button
            type="button"
            onClick={() => setCycle('annual')}
            className={`h-9 px-5 rounded-lg text-[13.5px] font-semibold transition flex items-center gap-2 ${
              cycle === 'annual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
            }`}
          >
            Yıllık
            <span
              className="uppercase text-[10px] tracking-[0.14em] px-1.5 py-0.5 rounded"
              style={{ ...monoStack, background: `color-mix(in oklch, ${ACCENT} 14%, white)`, color: ACCENT }}
            >
              2 ay hediye
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}

function PlanCard({ plan, cycle, isCurrent, isSubmitting, onClick }) {
  const isPopular = plan.popular;
  const ctaLabel = isSubmitting
    ? 'Yönlendiriliyor…'
    : isCurrent
    ? 'Mevcut plan'
    : plan.cta;

  return (
    <div
      className="relative rounded-2xl bg-white flex flex-col"
      style={{
        border: isPopular ? `1.5px solid ${ACCENT}` : '1px solid #e2e8f0',
        boxShadow: isPopular
          ? `0 20px 50px -20px color-mix(in oklch, ${ACCENT} 30%, transparent)`
          : '0 1px 2px rgba(0,0,0,0.02)',
      }}
    >
      {isPopular && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 h-6 px-2.5 rounded-full text-white uppercase text-[10px] tracking-[0.14em] flex items-center"
          style={{ ...monoStack, background: ACCENT }}
        >
          Popüler
        </div>
      )}
      {isCurrent && (
        <div
          className="absolute top-3 right-3 h-5 px-2 rounded-full uppercase text-[9.5px] tracking-[0.14em] flex items-center"
          style={{
            ...monoStack,
            background: '#e3f2ea',
            color: '#1a7f4f',
          }}
        >
          Aktif
        </div>
      )}
      <div className="p-6 flex-1 flex flex-col">
        <div className="uppercase text-[10px] tracking-[0.14em] text-slate-500" style={monoStack}>
          {plan.eyebrow}
        </div>
        <div className="mt-1 text-[22px] font-semibold text-slate-950 tracking-tight">{plan.name}</div>
        <div className="mt-4">
          <div className="flex items-baseline gap-1.5">
            <span
              className="text-[36px] font-semibold text-slate-950 tracking-[-0.02em]"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {plan.price[cycle]}
            </span>
          </div>
          <div className="mt-1 text-[12px] text-slate-500">{plan.priceNote}</div>
        </div>
        <div className="mt-3 text-[13px] text-slate-600 leading-[1.5] min-h-[40px]">{plan.desc}</div>

        <button
          type="button"
          onClick={onClick}
          disabled={isSubmitting || isCurrent}
          className={`mt-5 inline-flex items-center justify-center h-10 rounded-lg text-[13.5px] font-semibold transition ${
            isPopular
              ? 'text-white shadow-md hover:brightness-110'
              : 'text-slate-700 border border-slate-200 bg-white hover:bg-slate-50'
          } ${(isSubmitting || isCurrent) ? 'opacity-60 cursor-not-allowed' : ''}`}
          style={isPopular ? { background: ACCENT } : {}}
        >
          {ctaLabel}
        </button>

        <div className="mt-6 pt-5 border-t border-slate-100">
          <div className="uppercase text-[10px] tracking-[0.14em] text-slate-500 mb-3" style={monoStack}>
            Plan içeriği
          </div>
          <ul className="space-y-2.5">
            {plan.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-[13px] text-slate-700">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="mt-[3px] flex-shrink-0"
                  style={{ color: ACCENT }}
                >
                  <path
                    d="M3 8.5l3.5 3L13 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ComparisonTable() {
  const planNames = PLANS.map((p) => p.name);
  return (
    <section className="bg-slate-50 border-y border-slate-200 py-24">
      <div className="max-w-[1280px] mx-auto px-8">
        <div className="max-w-[640px] mb-10">
          <div className="uppercase text-[10.5px] tracking-[0.18em] text-slate-500 mb-3" style={monoStack}>
            — Karşılaştırma
          </div>
          <h2 className="text-[40px] md:text-[48px] leading-[1.05] tracking-[-0.025em] font-semibold text-slate-950">
            Tüm özellikler, <span className="text-slate-400">tek bakışta.</span>
          </h2>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-[13px]" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left p-5 w-[28%] uppercase text-[10.5px] tracking-[0.14em] text-slate-500" style={monoStack}>
                  Özellik
                </th>
                {planNames.map((n, i) => (
                  <th key={i} className="p-5 text-center font-semibold text-slate-900 text-[14px]">
                    {n}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((group, gi) => (
                <React.Fragment key={gi}>
                  <tr className="bg-slate-50/80">
                    <td colSpan={6} className="px-5 py-2.5 uppercase text-[10px] tracking-[0.16em] text-slate-600" style={monoStack}>
                      {group.group}
                    </td>
                  </tr>
                  {group.rows.map((row, ri) => (
                    <tr key={ri} className="border-t border-slate-100 hover:bg-slate-50/40 transition">
                      <td className="px-5 py-3 text-slate-700">{row.label}</td>
                      {row.values.map((v, vi) => (
                        <td key={vi} className="px-5 py-3 text-center">
                          {v === true ? (
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="inline" style={{ color: ACCENT }}>
                              <path
                                d="M3 8.5l3.5 3L13 5"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : v === false ? (
                            <span className="text-slate-300">—</span>
                          ) : (
                            <span className="text-slate-700 font-medium" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {v}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function EnterpriseBand() {
  return (
    <section className="bg-slate-950 text-white py-20 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />
      <div className="relative max-w-[1280px] mx-auto px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="uppercase text-[10.5px] tracking-[0.18em] text-slate-400 mb-4" style={monoStack}>
              — Kurumsal
            </div>
            <h2 className="text-[36px] md:text-[44px] leading-[1.08] tracking-[-0.025em] font-semibold">
              Özel ölçek, özel altyapı, özel destek.
            </h2>
            <p className="mt-5 text-[15.5px] text-slate-300 leading-[1.6] max-w-[600px]">
              500+ kullanıcı, çoklu workspace, yerinde kurulum, SSO, özel SLA ve KVKK danışmanlığı — kurumsal ihtiyaçlarınıza özel
              çözümler için ekibimizle görüşün.
            </p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {ENTERPRISE_TAGS.map((t, i) => (
                <span
                  key={i}
                  className="h-7 px-3 rounded-full border border-white/15 bg-white/[0.04] uppercase text-[10.5px] tracking-[0.12em] text-slate-300 flex items-center"
                  style={monoStack}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end">
            <a
              href="mailto:satis@onxflow.com?subject=Kurumsal%20Plan%20Talebi"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl text-[15px] font-semibold text-slate-950 bg-white hover:bg-slate-100 transition"
            >
              Kurumsal teklif alın
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M3 8h10m0 0L9 4m4 4l-4 4"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection({ openIdx, setOpenIdx }) {
  return (
    <section className="bg-white py-24">
      <div className="max-w-[1100px] mx-auto px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="uppercase text-[10.5px] tracking-[0.18em] text-slate-500 mb-3" style={monoStack}>
              — SSS
            </div>
            <h2 className="text-[36px] md:text-[44px] leading-[1.05] tracking-[-0.025em] font-semibold text-slate-950">
              Sıkça sorulan sorular.
            </h2>
            <p className="mt-5 text-[14.5px] text-slate-600 leading-[1.6]">
              Aradığınızı bulamadıysanız{' '}
              <a
                href="mailto:destek@onxflow.com"
                className="underline decoration-slate-300 underline-offset-4 hover:decoration-slate-700 transition"
              >
                ekibimizle iletişime geçin
              </a>
              .
            </p>
          </div>
          <div className="lg:col-span-8">
            <div className="border-t border-slate-200">
              {FAQ_ITEMS.map((item, i) => (
                <div key={i} className="border-b border-slate-200">
                  <button
                    type="button"
                    onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
                    className="w-full flex items-center justify-between gap-6 py-5 text-left group"
                  >
                    <span className="text-[16px] font-semibold text-slate-900 group-hover:text-slate-700 transition">
                      {item.q}
                    </span>
                    <span
                      className="w-6 h-6 rounded-full border border-slate-300 flex items-center justify-center flex-shrink-0 transition"
                      style={{ transform: openIdx === i ? 'rotate(45deg)' : 'none' }}
                    >
                      <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                        <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </span>
                  </button>
                  {openIdx === i && (
                    <div className="pb-6 text-[14.5px] text-slate-600 leading-[1.65] max-w-[640px]">{item.a}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function GuaranteeStrip() {
  return (
    <section className="bg-slate-50 border-y border-slate-200 py-12">
      <div className="max-w-[1280px] mx-auto px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
        {GUARANTEES.map((it, i) => (
          <div key={i} className="flex items-start gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `color-mix(in oklch, ${ACCENT} 12%, white)`, color: ACCENT }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 1.5l5.5 2.5v4c0 3-2.2 5.5-5.5 6.5C4.7 13.5 2.5 11 2.5 8V4L8 1.5z"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <div className="text-[14px] font-semibold text-slate-900">{it.t}</div>
              <div className="text-[12.5px] text-slate-500 mt-0.5">{it.d}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ClosingCTA({ user }) {
  const target = user ? '/dashboard' : '/';
  return (
    <section className="bg-white py-28 relative overflow-hidden">
      <div
        className="absolute pointer-events-none"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '900px',
          height: '500px',
          background: `radial-gradient(ellipse, color-mix(in oklch, ${ACCENT} 18%, transparent), transparent 65%)`,
          filter: 'blur(60px)',
        }}
      />
      <div className="relative max-w-[1100px] mx-auto px-8 text-center">
        <h2 className="text-[56px] md:text-[80px] leading-[0.98] tracking-[-0.030em] font-semibold text-slate-950" style={{ fontFamily: 'sans-serif' }}>
          Ekibinizle{' '}
          <span
            className="italic font-light"
            style={{
              background: `linear-gradient(90deg, ${ACCENT}, color-mix(in oklch, ${ACCENT} 55%, #6c4ad9))`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontFamily: 'Arial',
              letterSpacing: '0px',
            }}
          >
            bugün
          </span>{' '}
          başlayın.
        </h2>
        <p className="mt-6 text-[17px] text-slate-600 max-w-[560px] mx-auto leading-[1.55]">
          Kurulum dakikalar içinde tamamlanır. Kredi kartı gerekmez.
        </p>
        <div className="mt-9 flex items-center justify-center gap-3 flex-wrap">
          <Link
            to={target}
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl text-[15px] font-semibold text-white shadow-lg transition hover:brightness-110 hover:-translate-y-0.5"
            style={{ background: ACCENT, boxShadow: `0 6px 20px -8px ${ACCENT}` }}
          >
            {user ? 'Uygulamaya dön' : 'Ücretsiz başla'}
          </Link>
          <a
            href="mailto:satis@onxflow.com?subject=Demo%20Talebi"
            className="inline-flex items-center gap-2 h-12 px-5 rounded-xl text-[15px] font-medium text-slate-700 border border-slate-200 bg-white hover:bg-slate-50 transition"
          >
            Demo planlayın
          </a>
        </div>
      </div>
    </section>
  );
}

function PricingFooter() {
  return (
    <footer className="bg-slate-950 text-slate-400 pt-16 pb-10">
      <div className="max-w-[1280px] mx-auto px-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 pb-12 border-b border-white/10">
          <div className="col-span-2">
            <div className="flex items-center gap-2.5">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-[15px]"
                style={{ background: ACCENT }}
              >
                X
              </div>
              <div className="leading-none">
                <div className="font-bold text-[16px] text-white">OnxFlow</div>
                <div className="text-[9px] uppercase tracking-[0.14em] text-slate-500 mt-0.5" style={monoStack}>
                  Proje Yönetimi v2.0
                </div>
              </div>
            </div>
            <p className="mt-4 text-[13px] leading-[1.6] max-w-[300px]">
              Görev panosundan onaylara, raporlamadan rol yönetimine — ekipler için kurumsal proje platformu.
            </p>
          </div>
          <FooterCol
            title="Ürün"
            items={[
              { l: 'Görev Panosu', h: '/tasks' },
              { l: 'İş Akışları', h: '/workflows' },
              { l: 'Raporlar', h: '/reports' },
              { l: 'Fiyatlandırma', h: '/pricing' },
            ]}
          />
          <FooterCol
            title="Şirket"
            items={[
              { l: 'Hakkımızda', h: '#' },
              { l: 'İletişim', h: 'mailto:destek@onxflow.com' },
              { l: 'KVKK', h: '#' },
              { l: 'Şartlar', h: '#' },
            ]}
          />
          <FooterCol
            title="Destek"
            items={[
              { l: 'Yardım merkezi', h: '#' },
              { l: 'Sürüm notları', h: '#' },
              { l: 'Durum sayfası', h: '#' },
              { l: 'Geri bildirim', h: 'mailto:destek@onxflow.com' },
            ]}
          />
        </div>
        <div className="pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="uppercase text-[10.5px] tracking-[0.16em]" style={monoStack}>
            © 2026 OnxFlow · Tüm hakları saklıdır
          </div>
          <div className="flex items-center gap-2 uppercase text-[10.5px] tracking-[0.14em]" style={monoStack}>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
            <span>Tüm sistemler operasyonel</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, items }) {
  return (
    <div>
      <div className="uppercase text-[10px] tracking-[0.16em] text-slate-500 mb-4" style={monoStack}>
        {title}
      </div>
      <ul className="space-y-2.5 text-[13px]">
        {items.map((it, i) => {
          const isExternal = it.h.startsWith('mailto:') || it.h.startsWith('http') || it.h === '#';
          return (
            <li key={i}>
              {isExternal ? (
                <a href={it.h} className="hover:text-white transition">
                  {it.l}
                </a>
              ) : (
                <Link to={it.h} className="hover:text-white transition">
                  {it.l}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
